// A/B assignment for the card-on-file modal's "we will not charge you" copy
// (GT-15).
//
//   control     — today's copy (the baseline), byte-identical to what shipped.
//   reassurance — the same offer with the no-charge promise stated first and
//                 concretely ("$0 today", what the card is actually for)
//                 instead of as a trailing aside.
//
// WHY THIS ONE IS TESTED AND NOT JUST SHIPPED: GT-15 flags the card mechanic as
// the one most likely to backfire if the messaging is unclear, and its success
// metric is "zero support tickets about unexpected charges". Copy that reassures
// too hard reads as evasive; copy that under-explains reads as a trap. Neither
// is knowable in advance, hence the test before the default flips.
//
// Assignment is a stable hash of the Supabase user id — mirroring
// lib/billing/upgrade-copy-experiment.ts, lib/credits/finish-line-experiment.ts
// and lib/streak/experiment.ts — so a user lands in the same arm on every
// surface, session and device with no assignment table. It is resolved
// SERVER-SIDE (GET /api/credits) and handed to the client, so the bucketing
// logic and the kill switch never ship to the browser. Because the hash is
// deterministic, the server can re-derive a user's arm when stamping analytics
// without ever trusting a value the browser sent back.
//
// The hash is namespaced with its own `card_copy:` prefix so this split is
// INDEPENDENT of the other experiments' buckets — a user in finish_line or in
// the upgrade-copy variant is not thereby pushed into this arm, which would
// confound all three readouts.
//
// Rollout is controlled by the CARD_COPY_ROLLOUT env var so the test can be
// flipped without a deploy:
//   "off" | "control" → everyone on control (new copy dark).
//   "on"  | "all"     → everyone on the new copy (ship to 100%).
//   "experiment"      → an even 50/50 hash split (the A/B test).
//
// NOTE the deliberate difference from its siblings: when the var is UNSET this
// module runs the 50/50 split in production rather than defaulting the new copy
// on. GT-15 requires the test to conclude before the default rollout, so
// "nobody set the env var" must not silently become "shipped to 100%".
// Off-production still defaults to the new copy so it is visible locally and on
// preview branches without setting anything.

export type CardCopyVariant = "control" | "reassurance";

/** FNV-1a → an even bucket in [0, 100). Deterministic, no Math.random. */
function bucketPercent(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % 100;
}

export function resolveCardCopyVariant(
  userId: string | null | undefined,
): CardCopyVariant {
  const rollout = (process.env.CARD_COPY_ROLLOUT ?? "").trim().toLowerCase();

  // Explicit overrides win and don't need a user id.
  if (rollout === "off" || rollout === "control") return "control";
  if (rollout === "on" || rollout === "all") return "reassurance";

  // Bucketing needs a stable id; without one, fall back to the safe baseline
  // rather than a per-render coin flip that no analysis could join.
  if (!userId) return "control";

  if (rollout === "experiment") {
    return bucketPercent(`card_copy:${userId}`) < 50 ? "reassurance" : "control";
  }

  // Unset: new copy on off-production, real 50/50 split in production.
  if (process.env.NODE_ENV !== "production") return "reassurance";
  return bucketPercent(`card_copy:${userId}`) < 50 ? "reassurance" : "control";
}

/** Narrowing guard for values arriving from the wire / props. */
export function isCardCopyVariant(
  v: string | null | undefined,
): v is CardCopyVariant {
  return v === "control" || v === "reassurance";
}

/** True when the surface should render the reassurance copy (arm B). */
export function showsCardReassurance(
  v: CardCopyVariant | null | undefined,
): boolean {
  return v === "reassurance";
}

/**
 * Coerce anything off the wire into a usable arm. Unknown/missing → "control",
 * so a stale client or a failed fetch degrades to the baseline copy rather than
 * silently entering the test arm.
 */
export function coerceCardCopyVariant(
  v: string | null | undefined,
): CardCopyVariant {
  return isCardCopyVariant(v) ? v : "control";
}

/**
 * The copy itself, both arms in one place so the two can't drift — the same
 * reason lib/plans.ts owns the credit amounts. `credits` is always
 * CARD_ON_FILE_BONUS_CREDITS supplied by the caller; no arm may hardcode it.
 *
 * ACCURACY CONSTRAINTS on any future edit to these strings (GT-15's success
 * metric is zero "unexpected charge" support tickets, and the flow is a Stripe
 * SetupIntent — lib/billing/setup-card-session.ts, mode:"setup"):
 *   • MUST NOT claim a charge-and-refund ("we'll refund it") — nothing is
 *     captured, so there is nothing to refund.
 *   • MUST NOT quote a non-zero hold ("$1 hold") — the amount is $0.
 *   • MUST NOT promise the user will never be charged — a later subscription or
 *     top-up will charge this card. The promise is bounded to *today*.
 * Asserted in tests/unit/components/card-entry-modal.test.tsx.
 */
export function cardCopyFor(
  variant: CardCopyVariant,
  credits: number,
): { title: string; subtitle: string; bonusLine: string; noChargeLine: string } {
  if (variant === "reassurance") {
    return {
      title: `$0 today — unlock ${credits} credits`,
      subtitle:
        "We verify the card without charging it. Nothing is taken from your account today.",
      bonusLine: `+${credits} bonus credits`,
      noChargeLine:
        "$0 charged today — we only save the card so you can pay later if you choose",
    };
  }
  return {
    title: `Unlock ${credits} more credits`,
    subtitle: "Add a card to keep building — you won't be charged today.",
    bonusLine: `+${credits} bonus credits`,
    noChargeLine: "No charge today — card is only saved for later",
  };
}
